import { defineStore } from "pinia";
import axios from "axios";
import {
  BACKEND_URL,
  MARKET_OVERVIEW_PAGED_ENDPOINT,
  MARKET_OVERVIEW_EXPORT_LIMIT,
} from "../variables.js";
import { buildQueryPayload, normalizePagedResponse } from "../composables/marketOverviewQuery.js";

// Server-side paging for Market Overview.
//
// The request splits in two halves: the dataset *scope* (city / type / unittype
// / status / developer / date window) rides on the query string, because that is
// the shape the backend already parses and it is fixed for a given URL; the
// interactive *filters*, *sort* and page window go in the JSON body, because
// they are nested and change on every click. See marketOverviewQuery.js.
//
// Deliberately not persisted: this store holds one page of rows plus the
// aggregates for the current filter set. Both are server-derived and cheap to
// refetch — persisting them would serve stale rows after a reload and put a
// wide page of project data into localStorage for no benefit.
// POST the payload as a JSON string under a `text/plain` content type rather
// than letting axios tag it `application/json`.
//
// The endpoint answers a preflight with `Access-Control-Allow-Origin: *` but no
// `Access-Control-Allow-Headers`, so an `application/json` body — a non-safelisted
// content type, and therefore preflighted — is blocked by the browser and
// surfaces as an opaque axios "Network Error". `text/plain` is CORS-safelisted,
// so the request skips the preflight; the body is still JSON and the endpoint
// reads it from the raw request stream either way. Once the backend returns
// `Access-Control-Allow-Headers: Content-Type` for this route, this can go back
// to posting the object directly.
//
// Exported because every Market Overview POST needs it, not just this store's:
// the unit-type KPI card posts its filter payload from the view and hits the
// same preflight. One definition so the workaround is lifted in one place.
export const postPayload = (url, body) =>
  axios.post(url, JSON.stringify(body), {
    headers: { "Content-Type": "text/plain" },
  });

export const useMarketOverviewStore = defineStore("MarketOverviewStore", {
  state: () => ({
    loading: false,
    // Set to a user-facing string on failure; the component surfaces it.
    error: null,
    // Total rows matching the current filters across every page — drives the
    // row counter and Tabulator's pager. Not the length of the current page.
    total: 0,
    lastPage: 1,
    // { combined, forsale, rental } stat blocks computed server-side over the
    // whole filtered set. A page of 15 rows cannot produce these locally.
    aggregates: null,
    // Per-city rollup for the City Preview panel, same reason.
    cityBreakdown: [],
  }),

  actions: {
    endpoint(scopeQuery) {
      const base = `${BACKEND_URL}${MARKET_OVERVIEW_PAGED_ENDPOINT}`;
      return scopeQuery ? `${base}?${scopeQuery}` : base;
    },

    // One page of rows plus the aggregates for the whole filtered set.
    // Returns the normalized envelope; throws nothing — check `error`.
    async fetchPage({ scopeQuery = "", filters = {}, sort = [], page = 1, limit = 15 } = {}) {
      this.loading = true;
      this.error = null;
      try {
        const body = buildQueryPayload({ filters, sort, page, limit });
        const res = await postPayload(this.endpoint(scopeQuery), body);
        const out = normalizePagedResponse(res.data, { page, limit });
        this.total = out.total;
        this.lastPage = out.lastPage;
        this.aggregates = out.aggregates;
        this.cityBreakdown = out.cityBreakdown;
        return out;
      } catch (e) {
        console.error("Market Overview page request failed", e);
        this.error = "Failed to load projects. Please try again.";
        this.total = 0;
        this.lastPage = 1;
        return {
          rows: [],
          total: 0,
          lastPage: 1,
          page,
          aggregates: null,
          cityBreakdown: [],
        };
      } finally {
        this.loading = false;
      }
    },

    // Every row matching the current filters, ignoring the page window.
    //
    // Needed because three features are not page-scoped: the map (pins for all
    // matches), the City Preview rollup, and CSV/PDF export. Sent with an
    // explicit high limit rather than "no limit" so a runaway filter set can't
    // ask the backend for the entire table.
    async fetchAll({ scopeQuery = "", filters = {}, sort = [], limit = MARKET_OVERVIEW_EXPORT_LIMIT } = {}) {
      this.error = null;
      try {
        const body = buildQueryPayload({ filters, sort, page: 1, limit });
        const res = await postPayload(this.endpoint(scopeQuery), body);
        const out = normalizePagedResponse(res.data, { page: 1, limit });
        // Report truncation rather than silently exporting a subset.
        if (out.total > out.rows.length) {
          console.warn(
            `Market Overview: ${out.total} rows match but only ${out.rows.length} were returned (limit ${limit}).`
          );
        }
        // Outcome travels with the envelope, not only on `this.error`: the map's
        // full-set request and the table's page request run concurrently and
        // share that field, so a caller reading it could pick up the other
        // request's failure and discard a good result.
        return { ...out, error: null };
      } catch (e) {
        console.error("Market Overview full-set request failed", e);
        const message = "Failed to load the full result set. Please try again.";
        this.error = message;
        return {
          rows: [],
          total: 0,
          lastPage: 1,
          page: 1,
          aggregates: null,
          cityBreakdown: [],
          error: message,
        };
      }
    },

    // Unit-type averages per bucket and tenure, for the KPI card's tiles and the
    // Unit Analysis panel's charts (api/market_overview/unit_type_kpi). Every
    // tenure cell carries both the window's totals and the same figures per
    // Price Date, so the two screens read one request's worth of data.
    //
    // Scope and filters travel separately because the endpoint reads them
    // separately: scope on the query string, because the endpoint resolves the
    // row filters by building that same dataset and reading its own $_GET;
    // filters in the POST body, in the shape the paged endpoint takes. POST for
    // the same reason the paged request uses it — scope alone runs to ~80 city[]
    // values, and see postPayload above for the content type.
    //
    // Both tenures are always requested, so the panel's Sale / Lease toggle is a
    // local switch over one payload rather than a second round trip.
    //
    // `compare` asks for the equal-length window before `days` as well, which
    // costs the endpoint a second grouped pass — the card wants it for its
    // change figures, the panel plots the window itself and leaves it off.
    //
    // Returned rather than kept in state: both callers hold it for as long as
    // they are on screen, and it is nine buckets deep, which is not something
    // the rest of the page reads or should carry around.
    // `utcIds` is the alternative to `filters` for a caller whose grid is not
    // the Market Overview one: the Planning report filters its rows in the
    // browser, so it can name the unit type collections on screen outright
    // rather than describe them in a payload this endpoint would have to
    // re-resolve. Omitted when empty — the endpoint reads an empty list as no
    // constraint, so a caller with nothing on screen must skip the request.
    async fetchUnitTypeKpi({ scopeQuery = "", filters = {}, utcIds = null, days = 365, compare = false } = {}) {
      const url = `${BACKEND_URL}api/market_overview/unit_type_kpi${scopeQuery ? "?" + scopeQuery : ""
        }`;
      const body = {
        days: String(days),
        compare: compare ? "1" : "0",
        tenure: "both",
        filters,
      };
      if (utcIds && utcIds.length) body.utc_ids = utcIds;
      const { data } = await postPayload(url, body);
      if (!data || !data.data || !Array.isArray(data.data.unit_types)) {
        throw new Error("Unexpected unit_type_kpi response");
      }
      return data;
    },

    // The same unit-type figures per calendar period rather than as one windowed
    // aggregate (api/market_overview/unit_type_series), for the Unit Analysis
    // panel's charts. Scope, filters and the POST-as-text/plain workaround are
    // exactly as fetchUnitTypeKpi above; only the window and the grain differ.
    //
    // `from` is a fixed 'Y-m-d' start rather than a trailing window, so the
    // panel's axis keeps the same first period as the calendar moves on. The
    // endpoint snaps it back to its period's start and does the bucketing, so
    // every period comes back already grouped — there is nothing to re-bin here.
    async fetchUnitTypeSeries({ scopeQuery = "", filters = {}, from, group = "quarter" } = {}) {
      const url = `${BACKEND_URL}api/market_overview/unit_type_series${scopeQuery ? "?" + scopeQuery : ""
        }`;
      const { data } = await postPayload(url, {
        from,
        group,
        tenure: "both",
        filters,
      });
      if (!data || !data.data || !Array.isArray(data.data.unit_types)) {
        throw new Error("Unexpected unit_type_series response");
      }
      return data;
    },

    reset() {
      this.loading = false;
      this.error = null;
      this.total = 0;
      this.lastPage = 1;
      this.aggregates = null;
      this.cityBreakdown = [];
    },
  },
});
